<template>
    <div
        class="w-100 d-flex align-items-center py-16 cursor--pointer"
        @mousedown.prevent.left="mousedown"
        @touchstart="touchstart"
    >
        <time class="mr-14 user-select-none">
            {{ cookedCurrentTime }}
        </time>

        <progress-bar
            ref="bar"
            class="w-100 position-relative"
            background-color="dark"
            ignore-readability
            :min="0"
            :max="1"
            :smooth="false"
            :value="progress"
        >
            <div
                class="rounded-circle position-absolute abs-center-y bg-light"
                :style="handlerStyle"
            />
        </progress-bar>

        <time class="ml-14 user-select-none">
            {{ cookedDuration }}
        </time>
    </div>
</template>

<script>
import ProgressBar from "~/components/ProgressBar"
import {
    clamp,
    fmtDigitalTimeHoursMinsSecs,
    fmtDigitalTimeMinsSecs,
} from "~/lib/utils"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

export default {
    props: {
        currentTime: {
            type: Number,
            default: 0,
        },

        duration: {
            type: Number,
            default: 1,
        },
    },

    emits: ["input"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            isClicking: false,
        }
    },

    computed: {
        cookedDuration() {
            return this.formatTime(this.duration)
        },

        cookedCurrentTime() {
            return this.formatTime(this.currentTime)
        },

        progress() {
            if (!this.duration) {
                return 0
            }
            return this.currentTime / this.duration
        },

        handlerPosition() {
            return this.progress * 100
        },

        handlerStyle() {
            return {
                height: "8px",
                width: "8px",
                left: `calc(${this.handlerPosition}% - 3px)`,
            }
        },
    },

    beforeUnmount() {
        this.cleanup()
    },

    methods: {
        formatTime(seconds) {
            if (seconds >= 3600) {
                return fmtDigitalTimeHoursMinsSecs(seconds)
            }
            return fmtDigitalTimeMinsSecs(seconds)
        },

        mousedown(event) {
            this._removeMouseMove = this.listen("mousemove", this.mousemove)
            this._removeMouseUp = this.listen("mouseup", this.mouseup)

            const { left, width } = this.$refs.bar.$el.getBoundingClientRect()
            const offsetX = event.clientX - left
            const newValue = clamp(offsetX / width, 0, 1)
            this.$emit("input", newValue * this.duration)
        },

        mousemove(event) {
            const { left, width } = this.$refs.bar.$el.getBoundingClientRect()
            const offsetX = event.clientX - left
            const newValue = clamp(offsetX / width, 0, 1)
            this.$emit("input", newValue * this.duration)
        },

        mouseup() {
            this.cleanup()
        },

        touchstart(event) {
            if (event.touches.length != 1) return // We may be gesturing
            const touch = Array.from(event.targetTouches)[0]
            if (!touch) return

            this._initialTouchId = touch.identifier

            this._removeTouchMove = this.listen("touchmove", this.touchmove)
            this._removeTouchEnd = this.listen("touchend", this.touchend)

            const { left, width } = this.$refs.bar.$el.getBoundingClientRect()
            const offsetX = touch.clientX - left
            const newValue = clamp(offsetX / width, 0, 1)
            this.$emit("input", newValue * this.duration)
        },

        touchmove(event) {
            const touch = Array.from(event.touches).find(
                t => t.identifier == this._initialTouchId
            )
            if (!touch) return

            const { left, width } = this.$refs.bar.$el.getBoundingClientRect()
            const offsetX = touch.clientX - left
            const newValue = clamp(offsetX / width, 0, 1)
            this.$emit("input", newValue * this.duration)
        },

        touchend() {
            this.cleanup()
        },

        cleanup() {
            this._removeMouseMove && this._removeMouseMove()
            this._removeMouseUp && this._removeMouseUp()
            this._removeTouchMove && this._removeTouchMove()
            this._removeTouchEnd && this._removeTouchEnd()
        },
    },

    components: {
        ProgressBar,
    },
}
</script>
